import useLatest from "@-/com.npmjs.use-latest";
import { boundedPromise } from "@-/dev.improve.haniverse.lib.deadline";
import { animationFrames } from "@-/dev.improve.haniverse.lib.scheduler";
import { useEffect } from "react";

/**
 * Past this, the loading skeleton gives way without a first frame (and the expiry is reported):
 * a renderer that cannot draw shows its empty canvas rather than a silhouette that never clears.
 * A first frame normally lands within a few frames; a cold Graphite pipeline compile takes longer.
 */
const CHINA_MAP_FIRST_FRAME_DEADLINE_MS = 3000;

/**
 * Calls `onFirstFrame` once after mount, on the first animation frame at which `isPresented()`
 * holds, or at the deadline. The canvas keeps the loading skeleton under the renderer until then.
 */
export function useChinaMapFirstFrame(isPresented: () => boolean, onFirstFrame: () => void) {
  const latestIsPresented = useLatest(isPresented);
  const latestOnFirstFrame = useLatest(onFirstFrame);
  useEffect(() => {
    let isMounted = true;
    let stop = () => {};
    void boundedPromise(
      {
        deadline: CHINA_MAP_FIRST_FRAME_DEADLINE_MS,
        // A hold: the skeleton gives way either way.
        onDeadline: "resolve",
        operation: "China map first frame",
      },
      (resolve) => {
        stop = resolve;
        let isSettled = false;
        const poll = async () => {
          for await (const _frame of animationFrames()) {
            if (isSettled) {
              return;
            }
            if (latestIsPresented.current()) {
              resolve();
              return;
            }
          }
        };
        void poll();
        return () => {
          isSettled = true;
        };
      },
    ).then(() => {
      if (isMounted) {
        latestOnFirstFrame.current();
      }
    });
    return () => {
      isMounted = false;
      stop();
    };
  }, [latestIsPresented, latestOnFirstFrame]);
}
